import type { PlanogramItem, PlanogramItemOrientation } from "@CommonTypes/merchflow/pog/item";
import type { PlanogramContainer } from "@CommonTypes/merchflow/pog/container";
import type { PlanogramPegboard } from "@CommonTypes/merchflow/pog/pegboard";
import type { Pog } from "@CommonTypes/merchflow/pog/pog";
import { getCappingSpanCount } from "../../common/planogramFacing";
import { resolvePlanogramProductLabel } from "../../common/planogramItemLabel";
import { getPegboardHoleCounts } from "../../common/pegboardHoleCounts";
import { getPegboardXLeft } from "../../common/pegboardPosition";
import { getPegboardFacingChangeColor } from "../Pegboards/pegboardFacingChange";
import type {
  PlanogramProductCell,
  PlanogramProductCellKind,
  PlanogramProductLabel,
  PlanogramSceneContainer,
  PlanogramScene,
  PlanogramSceneHole,
  PlanogramScenePegboard,
  PlanogramScenePlacement,
  PlanogramScenePlacementCell,
  PlanogramSceneProduct,
  PlanogramWorldRect,
} from "./types";
import {
  getMergedMerchandisableWidth,
  getShelfItemWidth,
  getShelfItemXOffsets,
} from "@CommonUtils/pog/shelfMerchandising";

const getIdentityFallback = (name: string, productCode: string, stableId: string) =>
  [name, productCode, stableId].find((value) => value.trim().length > 0) ?? stableId;

const getLabel = (product: object, label: PlanogramProductLabel, fallback: string) =>
  label === "none" ? fallback : resolvePlanogramProductLabel(product, label, fallback);

const getProductLabel = (item: PlanogramItem, label: PlanogramProductLabel) =>
  getLabel(item, label, getIdentityFallback(item.name, item.productCode, item.uniqueId));

const isNewInsertedItem = (item: PlanogramItem) =>
  item.updateStatus?.operation === "INSERT" && item.updateStatus?.changeType === "INSERTED";

interface CellPosition {
  column: number;
  row: number;
}

const createCell = (
  item: PlanogramItem,
  kind: PlanogramProductCellKind,
  orientation: PlanogramItemOrientation,
  position: CellPosition,
): PlanogramProductCell => ({
  column: position.column,
  height: item.getOrientedDimensionForOrientation("height", orientation),
  kind,
  orientation,
  row: position.row,
  unitsHigh: 1,
  unitsWide: 1,
  width: item.getOrientedDimensionForOrientation("width", orientation),
  x: 0,
  y: 0,
});

const projectProductCells = (item: PlanogramItem) => {
  const cells: PlanogramProductCell[] = [];
  const mainWidth = item.getWidth();
  const mainHeight = item.getHeight();
  const horizontalFacings = item.getHorizontalFacings();
  const verticalFacings = item.getVerticalFacings();
  const mainWidthSpan = mainWidth * horizontalFacings;
  const mainHeightSpan = mainHeight * verticalFacings;

  const yCapOrientation = item.yCapOrientation;
  const yCapWidth = item.getOrientedDimensionForOrientation("width", yCapOrientation);
  const yCapHeight = item.getOrientedDimensionForOrientation("height", yCapOrientation);
  const yCapColumns = getCappingSpanCount(mainWidthSpan, yCapWidth);
  const yCappingHeight = item.getYCappingHeight();

  if (item.yCapNum > 0) {
    for (let index = 0; index < item.yCapNum * yCapColumns; index += 1) {
      const column = Math.floor(index / item.yCapNum);
      const row = index % item.yCapNum;
      const cell = createCell(item, "y-cap", yCapOrientation, { column, row });

      cells.push({
        ...cell,
        height: yCapHeight,
        width: yCapWidth,
        x: column * yCapWidth,
        y: row * yCapHeight,
      });
    }
  }

  for (let index = 0; index < item.facings; index += 1) {
    const column = Math.floor(index / verticalFacings);
    const row = index % verticalFacings;
    const cell = createCell(item, "main", item.orientation, { column, row });

    cells.push({
      ...cell,
      height: mainHeight,
      width: mainWidth,
      x: column * mainWidth,
      y: yCappingHeight + row * mainHeight,
      unitsHigh: item.getUnitsHigh(),
      unitsWide: item.getUnitsWide(),
    });
  }

  const xCapOrientation = item.xCapOrientation;
  const xCapWidth = item.getOrientedDimensionForOrientation("width", xCapOrientation);
  const xCapHeight = item.getOrientedDimensionForOrientation("height", xCapOrientation);
  const xCapRows = getCappingSpanCount(mainHeightSpan, xCapHeight);
  const xCapTop = yCappingHeight + mainHeightSpan - xCapRows * xCapHeight;

  if (item.xCapNum > 0) {
    for (let index = 0; index < item.xCapNum * xCapRows; index += 1) {
      const column = Math.floor(index / xCapRows);
      const row = index % xCapRows;
      const cell = createCell(item, "x-cap", xCapOrientation, { column, row });

      cells.push({
        ...cell,
        height: xCapHeight,
        width: xCapWidth,
        x: mainWidthSpan + column * xCapWidth,
        y: xCapTop + row * xCapHeight,
      });
    }
  }

  return cells;
};

const projectPegboard = (
  pegboard: PlanogramPegboard,
  sceneHeight: number,
  width: number,
): PlanogramScenePegboard => {
  const pegboardXLeft = getPegboardXLeft(pegboard);
  const counts = getPegboardHoleCounts({
    width,
    height: pegboard.height,
    holesOffsetHorizontal: pegboard.holesOffsetHorizontal,
    holesOffsetVertical: pegboard.holesOffsetVertical,
    holesGapHorizontal: pegboard.holesGapHorizontal,
    holesGapVertical: pegboard.holesGapVertical,
  });
  const holes = Array.from({ length: counts.horizontal * counts.vertical }, (_, index) => {
    const column = index % counts.horizontal;
    const row = Math.floor(index / counts.horizontal);
    return {
      column,
      height: 2,
      row,
      width: 2,
      x: pegboardXLeft + pegboard.holesOffsetHorizontal + column * pegboard.holesGapHorizontal - 1,
      y:
        sceneHeight -
        pegboard.yBottom -
        pegboard.height +
        pegboard.holesOffsetVertical +
        row * pegboard.holesGapVertical -
        1,
    };
  });

  return {
    bayId: pegboard.bay.uniqueId,
    height: pegboard.height,
    holeGapHorizontal: pegboard.holesGapHorizontal,
    holeGapVertical: pegboard.holesGapVertical,
    holeOffsetHorizontal: pegboard.holesOffsetHorizontal,
    holeOffsetVertical: pegboard.holesOffsetVertical,
    holes,
    id: pegboard.uniqueId,
    width,
    x: pegboardXLeft,
    y: sceneHeight - pegboard.yBottom - pegboard.height,
  };
};

const projectContainer = (
  container: PlanogramContainer,
  sceneHeight: number,
): PlanogramSceneContainer => ({
  color: container.color,
  height: container.getHeight(),
  id: container.uniqueId,
  label: container.getLabel() || container.uniqueId,
  shelfType: container.shelfType,
  width: container.getWidth(),
  x: container.xLeft,
  y: sceneHeight - container.yTop,
});

const projectPlacement = (
  pegboard: PlanogramPegboard,
  placement: PlanogramPegboard["placements"][number],
  sceneHeight: number,
  label: PlanogramProductLabel,
): PlanogramScenePlacement | null => {
  const pegboardXLeft = getPegboardXLeft(pegboard);
  const width = placement.pegProduct.unitWidth;
  const height = placement.pegProduct.unitHeight;
  const absoluteFacingCells = placement.holes.map<PlanogramScenePlacementCell>((hole) => ({
    depth: hole.depth,
    height,
    holeNoLeft: hole.holeNoLeft,
    holeNoTop: hole.holeNoTop,
    width,
    x:
      pegboardXLeft +
      pegboard.holesOffsetHorizontal +
      (hole.holeNoLeft - 1) * pegboard.holesGapHorizontal,
    y:
      sceneHeight -
      pegboard.yBottom -
      pegboard.height +
      pegboard.holesOffsetVertical +
      (hole.holeNoTop - 1) * pegboard.holesGapVertical,
  }));
  const firstFacingCell = absoluteFacingCells[0];
  if (!firstFacingCell) return null;

  const x = absoluteFacingCells.reduce(
    (minimum, cell) => Math.min(minimum, cell.x),
    firstFacingCell.x,
  );
  const y = absoluteFacingCells.reduce(
    (minimum, cell) => Math.min(minimum, cell.y),
    firstFacingCell.y,
  );
  const right = absoluteFacingCells.reduce(
    (maximum, cell) => Math.max(maximum, cell.x + cell.width),
    firstFacingCell.x + firstFacingCell.width,
  );
  const bottom = absoluteFacingCells.reduce(
    (maximum, cell) => Math.max(maximum, cell.y + cell.height),
    firstFacingCell.y + firstFacingCell.height,
  );
  const facingCells = absoluteFacingCells.map((cell) => ({
    ...cell,
    x: cell.x - x,
    y: cell.y - y,
  }));

  return {
    facingChangeColor: getPegboardFacingChangeColor(placement.pegProduct),
    facingChangeType: placement.pegProduct.updateStatus?.changeType ?? null,
    facingCells,
    height: bottom - y,
    id: placement.uniqueId,
    label: getLabel(
      placement.pegProduct,
      label,
      getIdentityFallback(
        placement.pegProduct.name,
        placement.pegProduct.productCode,
        placement.uniqueId,
      ),
    ),
    name: placement.pegProduct.name,
    pegboardId: pegboard.uniqueId,
    productCode: placement.pegProduct.productCode,
    productId: placement.pegProduct.id,
    width: right - x,
    x,
    y,
  };
};

interface ItemCoordinates {
  readonly xLeft: number;
  readonly xRight: number;
  readonly yBottom: number;
  readonly yTop: number;
}

const projectProduct = (
  item: PlanogramItem,
  coordinates: ItemCoordinates,
  shelfGroupId: string,
  sceneHeight: number,
  label: PlanogramProductLabel,
): PlanogramSceneProduct => {
  return {
    cells: projectProductCells(item),
    height: coordinates.yTop - coordinates.yBottom,
    horizontalFacings: item.getHorizontalFacings(),
    id: item.uniqueId,
    isNewInsertedItem: isNewInsertedItem(item),
    label: getProductLabel(item, label),
    name: item.name,
    orientation: item.orientation,
    productCode: item.productCode,
    shelfGroupId,
    shelfId: item.shelf.uniqueId,
    validOrientations: [...item.validOrientations],
    verticalFacings: item.getVerticalFacings(),
    width: coordinates.xRight - coordinates.xLeft,
    x: coordinates.xLeft,
    y: sceneHeight - coordinates.yTop,
  };
};

interface ShelfCoordinates {
  readonly xLeft: number;
  readonly yBottom: number;
  readonly yTop: number;
}

interface ProjectionGeometry {
  readonly bayXLeft: Map<string, number>;
  readonly itemCoordinates: Map<string, ItemCoordinates>;
  readonly shelfCoordinates: Map<string, ShelfCoordinates>;
  readonly shelfGroupIds: Map<string, string>;
  readonly coordinateWorkCount: number;
  readonly height: number;
  readonly width: number;
}

const createProjectionGeometry = (pog: Pog): ProjectionGeometry => {
  const bayXLeft = new Map<string, number>();
  const shelfCoordinates = new Map<string, ShelfCoordinates>();
  const shelfGroupIds = new Map<string, string>();
  const itemCoordinates = new Map<string, ItemCoordinates>();
  let coordinateWorkCount = 0;
  let xLeft = 0;
  let maxItemYTop = 0;
  let maxItemXRight = 0;

  for (const bay of pog.planogram.bays) {
    bayXLeft.set(bay.uniqueId, xLeft);
    xLeft += bay.width;
    for (let index = 0; index < bay.shelves.length; index += 1) {
      const shelf = bay.shelves[index];
      if (!shelf) continue;
      const previousShelf = bay.shelves[index - 1];
      const yBottom = shelf.getYBottom();
      shelfCoordinates.set(shelf.uniqueId, {
        xLeft: bayXLeft.get(bay.uniqueId) ?? 0,
        yBottom,
        yTop: previousShelf ? previousShelf.getYBottom() : bay.height,
      });
    }
  }

  const allShelves = pog.planogram.bays.flatMap((bay) => bay.shelves);
  const shelvesById = new Map(allShelves.map((shelf) => [shelf.uniqueId, shelf]));
  const groupRoots = allShelves.filter((shelf) => !shelf.mergedLeft);
  for (const root of groupRoots) {
    const groupId = root.uniqueId;
    const groupShelves: typeof allShelves = [];
    const seen = new Set<string>();
    let shelf: (typeof allShelves)[number] | null = root;
    while (shelf && !seen.has(shelf.uniqueId)) {
      seen.add(shelf.uniqueId);
      groupShelves.push(shelf);
      shelfGroupIds.set(shelf.uniqueId, groupId);
      shelf = shelf.mergedRight ? (shelvesById.get(shelf.mergedRight.uniqueId) ?? null) : null;
    }

    const firstShelf = groupShelves[0];
    if (!firstShelf) continue;
    const firstCoordinates = shelfCoordinates.get(firstShelf.uniqueId);
    if (!firstCoordinates) continue;
    const mergedItems = groupShelves.flatMap((member) => member.items);
    const itemWidths = mergedItems.map((item) => {
      coordinateWorkCount += 1;
      return getShelfItemWidth(item);
    });
    const itemXOffsets = getShelfItemXOffsets({
      itemWidths,
      merchandisableWidth: getMergedMerchandisableWidth(groupShelves),
      overflowPolicy: "extend-on-overflow",
    });
    for (let itemIndex = 0; itemIndex < mergedItems.length; itemIndex += 1) {
      const item = mergedItems[itemIndex];
      coordinateWorkCount += 1;
      const itemWidth = itemWidths[itemIndex];
      const itemHeight = item.getHeight() * item.getVerticalFacings() + item.getYCappingHeight();
      const coordinates = shelfCoordinates.get(item.shelf.uniqueId);
      if (!coordinates) continue;
      const itemX = firstCoordinates.xLeft + itemXOffsets[itemIndex];
      const coordinatesForItem = {
        xLeft: itemX,
        xRight: itemX + itemWidth,
        yBottom: coordinates.yBottom + item.shelf.thickness,
        yTop: coordinates.yBottom + item.shelf.thickness + itemHeight,
      };
      itemCoordinates.set(item.uniqueId, coordinatesForItem);
      shelfGroupIds.set(item.shelf.uniqueId, groupId);
      maxItemYTop = Math.max(maxItemYTop, coordinatesForItem.yTop);
      maxItemXRight = Math.max(maxItemXRight, coordinatesForItem.xRight);
    }
  }

  const structuralHeight = pog.planogram.getHeight();
  return {
    bayXLeft,
    coordinateWorkCount,
    height: Math.max(structuralHeight, maxItemYTop),
    itemCoordinates,
    shelfCoordinates,
    shelfGroupIds,
    width: Math.max(xLeft, maxItemXRight),
  };
};

interface UncachedProjection {
  /** Number of item coordinate/projection operations performed during geometry projection. */
  readonly coordinateCount: number;
  readonly scene: PlanogramScene;
}

const projectPlanogramSceneUncached = (
  pog: Pog,
  label: PlanogramProductLabel = "name",
): UncachedProjection => {
  const geometry = createProjectionGeometry(pog);
  const { height } = geometry;
  const bays: PlanogramScene["bays"] = [];
  const containers: PlanogramScene["containers"] = pog.containers.map((container) =>
    projectContainer(container, height),
  );
  const shelves: PlanogramScene["shelves"] = [];
  const fixtures: PlanogramScene["fixtures"] = [];
  const pegboards: PlanogramScenePegboard[] = [];
  const placements: PlanogramScenePlacement[] = [];
  const products: PlanogramScene["products"] = [];
  const unsupportedFixtureIds: string[] = [];

  for (let bayIndex = 0; bayIndex < pog.planogram.bays.length; bayIndex += 1) {
    const bay = pog.planogram.bays[bayIndex];
    if (!bay) continue;
    const bayX = geometry.bayXLeft.get(bay.uniqueId) ?? 0;
    const bayHeight = Math.max(bay.height, bay.notchOffset + bay.maxNotch * bay.notchSpacing);

    bays.push({
      baseHeight: bay.baseHeight,
      bayNo: bayIndex + 1,
      height: bayHeight,
      id: bay.uniqueId,
      maxNotch: bay.maxNotch,
      notchOffset: bay.notchOffset,
      notchSpacing: bay.notchSpacing,
      width: bay.width,
      x: bayX,
      y: height - bayHeight,
    });

    for (const pegboard of bay.pegboards) {
      pegboards.push(projectPegboard(pegboard, height, bay.width));
      for (const placement of pegboard.placements) {
        const scenePlacement = projectPlacement(pegboard, placement, height, label);
        if (scenePlacement) placements.push(scenePlacement);
      }
    }

    for (let shelfIndex = 0; shelfIndex < bay.shelves.length; shelfIndex += 1) {
      const shelf = bay.shelves[shelfIndex];
      if (!shelf) continue;
      const coordinates = geometry.shelfCoordinates.get(shelf.uniqueId);
      if (!coordinates) continue;
      const shelfGroupId = geometry.shelfGroupIds.get(shelf.uniqueId) ?? shelf.uniqueId;
      if (shelf.type !== "REGULAR") {
        // HANGCELL products hang below the fixture (down to the shelf below, or bay floor);
        // RISER products sit above it (up to the shelf above, or bay top edge), same as REGULAR.
        // For HANGCELL this equals height/y below, since those already exclude the bar thickness.
        const nonMerchandisableExtensionHeight =
          shelf.type === "HANGCELL"
            ? shelf.getHeight()
            : coordinates.yTop - coordinates.yBottom - shelf.thickness;
        const nonMerchandisableExtensionY =
          shelf.type === "HANGCELL" ? height - coordinates.yBottom : height - coordinates.yTop;

        fixtures.push({
          bayId: bay.uniqueId,
          fixtureType: shelf.type,
          height: shelf.type === "HANGCELL" ? shelf.getHeight() : shelf.thickness,
          id: shelf.uniqueId,
          merchandisableWidth: shelf.getMerchandisableWidth(),
          nonMerchandisableExtensionHeight,
          nonMerchandisableExtensionY,
          shelfNo: shelfIndex + 1,
          thickness: shelf.thickness,
          width: shelf.width,
          x: coordinates.xLeft,
          y:
            shelf.type === "HANGCELL"
              ? height - coordinates.yBottom
              : height - coordinates.yBottom - shelf.thickness,
        });
        if (shelf.type !== "HANGCELL") {
          unsupportedFixtureIds.push(shelf.uniqueId);
        }
        if (shelf.type === "HANGCELL") {
          for (const item of shelf.items) {
            const itemCoordinates = geometry.itemCoordinates.get(item.uniqueId);
            if (itemCoordinates) {
              products.push(projectProduct(item, itemCoordinates, shelfGroupId, height, label));
            }
          }
        }
        continue;
      }

      shelves.push({
        bayId: bay.uniqueId,
        groupId: shelfGroupId,
        height: shelf.thickness,
        id: shelf.uniqueId,
        merchandisableWidth: shelf.getMerchandisableWidth(),
        // Extension zone beyond the shelf's own thickness: up to the shelf above, or bay top edge.
        nonMerchandisableExtensionHeight: coordinates.yTop - coordinates.yBottom - shelf.thickness,
        nonMerchandisableExtensionY: height - coordinates.yTop,
        shelfNo: shelfIndex + 1,
        thickness: shelf.thickness,
        width: shelf.width,
        x: coordinates.xLeft,
        y: height - coordinates.yBottom - shelf.thickness,
      });

      for (const item of shelf.items) {
        const itemCoordinates = geometry.itemCoordinates.get(item.uniqueId);
        if (itemCoordinates) {
          products.push(projectProduct(item, itemCoordinates, shelfGroupId, height, label));
        }
      }
    }
  }

  const productWidth = products.reduce(
    (maxWidth, product) => Math.max(maxWidth, product.x + product.width),
    0,
  );
  const placementWidth = placements.reduce(
    (maxWidth, placement) => Math.max(maxWidth, placement.x + placement.width),
    0,
  );

  return {
    coordinateCount: geometry.coordinateWorkCount,
    scene: {
      bays,
      containers,
      fixtures,
      height,
      pegboards,
      placements,
      products,
      shelves,
      unsupportedFixtureIds: [...new Set(unsupportedFixtureIds)],
      width: Math.max(geometry.width, productWidth, placementWidth),
    },
  };
};

export interface PlanogramSceneProjectionStats {
  readonly cacheEntryCount: number;
  /** Number of item coordinate/projection operations in the latest projection. */
  readonly coordinateCount: number;
  readonly createdEntityCount: number;
  readonly entityCount: number;
  readonly projectionCount: number;
  readonly reusedEntityCount: number;
}

export interface PlanogramSceneProjector {
  readonly getStats: () => PlanogramSceneProjectionStats;
  readonly project: (pog: Pog, label?: PlanogramProductLabel) => PlanogramScene;
}

type SceneEntity =
  | PlanogramScene["bays"][number]
  | PlanogramScene["containers"][number]
  | PlanogramScene["fixtures"][number]
  | NonNullable<PlanogramScene["pegboards"]>[number]
  | NonNullable<PlanogramScene["placements"]>[number]
  | PlanogramScene["products"][number]
  | PlanogramScene["shelves"][number];

interface CachedSceneEntity<T extends SceneEntity> {
  readonly signature: string;
  readonly value: T;
}

const getWorldRectSignature = ({ height, width, x, y }: PlanogramWorldRect) =>
  `${x},${y},${width},${height}`;

const getCellSignature = (cell: PlanogramProductCell) =>
  [
    cell.column,
    cell.height,
    cell.kind,
    cell.orientation,
    cell.row,
    cell.unitsHigh,
    cell.unitsWide,
    cell.width,
    cell.x,
    cell.y,
  ].join(",");

const getPlacementCellSignature = (cell: PlanogramScenePlacementCell) =>
  [cell.depth, cell.holeNoLeft, cell.holeNoTop, getWorldRectSignature(cell)].join(",");

const getHoleSignature = (hole: PlanogramSceneHole) =>
  [hole.column, hole.row, getWorldRectSignature(hole)].join(",");

const getSceneEntitySignature = (entity: SceneEntity): string => {
  const worldRect = getWorldRectSignature(entity);

  if ("cells" in entity) {
    return [
      worldRect,
      entity.horizontalFacings,
      entity.isNewInsertedItem,
      entity.label,
      entity.name,
      entity.orientation,
      entity.productCode,
      entity.shelfGroupId,
      entity.shelfId,
      entity.validOrientations.join(","),
      entity.verticalFacings,
      entity.cells.map(getCellSignature).join(";"),
    ].join("|");
  }

  if ("holes" in entity) {
    return [
      worldRect,
      entity.bayId,
      entity.holeGapHorizontal,
      entity.holeGapVertical,
      entity.holeOffsetHorizontal,
      entity.holeOffsetVertical,
      entity.holes.map(getHoleSignature).join(";"),
    ].join("|");
  }

  if ("pegboardId" in entity) {
    return [
      worldRect,
      entity.facingChangeColor,
      entity.facingChangeType,
      entity.facingCells.map(getPlacementCellSignature).join(";"),
      entity.label,
      entity.name,
      entity.pegboardId,
      entity.productCode,
      entity.productId,
    ].join("|");
  }

  if ("fixtureType" in entity) {
    return [
      worldRect,
      entity.bayId,
      entity.fixtureType,
      entity.merchandisableWidth,
      entity.shelfNo,
      entity.thickness,
    ].join("|");
  }

  if ("groupId" in entity) {
    return [
      worldRect,
      entity.bayId,
      entity.groupId,
      entity.merchandisableWidth,
      entity.shelfNo,
      entity.thickness,
    ].join("|");
  }

  if ("shelfType" in entity) {
    return [worldRect, entity.color, entity.label, entity.shelfType].join("|");
  }

  return [
    worldRect,
    entity.baseHeight,
    entity.bayNo,
    entity.maxNotch,
    entity.notchOffset,
    entity.notchSpacing,
  ].join("|");
};

const reuseEntities = <T extends SceneEntity>(
  entities: readonly T[],
  cache: Map<string, CachedSceneEntity<T>>,
  stats: { createdEntityCount: number; reusedEntityCount: number },
): T[] =>
  entities.map((entity) => {
    const signature = getSceneEntitySignature(entity);
    const cached = cache.get(entity.id);
    if (cached?.signature === signature) {
      stats.reusedEntityCount += 1;
      return cached.value;
    }
    stats.createdEntityCount += 1;
    cache.set(entity.id, { signature, value: entity });
    return entity;
  });

const pruneEntityCache = <T extends SceneEntity>(
  entities: readonly T[],
  cache: Map<string, CachedSceneEntity<T>>,
) => {
  const liveIds = new Set(entities.map((entity) => entity.id));
  for (const id of cache.keys()) {
    if (!liveIds.has(id)) cache.delete(id);
  }
};

/**
 * Projects immutable POG snapshots while retaining unchanged entity entries by stable ID.
 * The input traversal is intentionally one pass over each projected collection; cache lookup
 * and signature work are bounded by the size of the entry being projected.
 */
export const createPlanogramSceneProjector = (): PlanogramSceneProjector => {
  const caches = {
    bays: new Map<string, CachedSceneEntity<PlanogramScene["bays"][number]>>(),
    containers: new Map<string, CachedSceneEntity<PlanogramScene["containers"][number]>>(),
    fixtures: new Map<string, CachedSceneEntity<PlanogramScene["fixtures"][number]>>(),
    pegboards: new Map<
      string,
      CachedSceneEntity<NonNullable<PlanogramScene["pegboards"]>[number]>
    >(),
    placements: new Map<
      string,
      CachedSceneEntity<NonNullable<PlanogramScene["placements"]>[number]>
    >(),
    products: new Map<string, CachedSceneEntity<PlanogramScene["products"][number]>>(),
    shelves: new Map<string, CachedSceneEntity<PlanogramScene["shelves"][number]>>(),
  };
  let lastPog: Pog | null = null;
  let lastLabel: PlanogramProductLabel | null = null;
  let lastScene: PlanogramScene | null = null;
  let stats: PlanogramSceneProjectionStats = {
    cacheEntryCount: 0,
    coordinateCount: 0,
    createdEntityCount: 0,
    entityCount: 0,
    projectionCount: 0,
    reusedEntityCount: 0,
  };

  const project = (pog: Pog, label: PlanogramProductLabel = "name") => {
    if (pog === lastPog && label === lastLabel && lastScene) return lastScene;

    const projectedResult = projectPlanogramSceneUncached(pog, label);
    const projected = projectedResult.scene;
    const counts = { createdEntityCount: 0, reusedEntityCount: 0 };
    const scene: PlanogramScene = {
      ...projected,
      bays: reuseEntities(projected.bays, caches.bays, counts),
      containers: reuseEntities(projected.containers, caches.containers, counts),
      fixtures: reuseEntities(projected.fixtures, caches.fixtures, counts),
      pegboards: reuseEntities(projected.pegboards ?? [], caches.pegboards, counts),
      placements: reuseEntities(projected.placements ?? [], caches.placements, counts),
      products: reuseEntities(projected.products, caches.products, counts),
      shelves: reuseEntities(projected.shelves, caches.shelves, counts),
    };
    pruneEntityCache(scene.bays, caches.bays);
    pruneEntityCache(scene.containers, caches.containers);
    pruneEntityCache(scene.fixtures, caches.fixtures);
    pruneEntityCache(scene.pegboards ?? [], caches.pegboards);
    pruneEntityCache(scene.placements ?? [], caches.placements);
    pruneEntityCache(scene.products, caches.products);
    pruneEntityCache(scene.shelves, caches.shelves);
    const entityCount =
      scene.bays.length +
      scene.containers.length +
      scene.fixtures.length +
      (scene.pegboards?.length ?? 0) +
      (scene.placements?.length ?? 0) +
      scene.products.length +
      scene.shelves.length;
    stats = {
      cacheEntryCount:
        caches.bays.size +
        caches.containers.size +
        caches.fixtures.size +
        caches.pegboards.size +
        caches.placements.size +
        caches.products.size +
        caches.shelves.size,
      coordinateCount: projectedResult.coordinateCount,
      createdEntityCount: counts.createdEntityCount,
      entityCount,
      projectionCount: stats.projectionCount + 1,
      reusedEntityCount: counts.reusedEntityCount,
    };
    lastLabel = label;
    lastPog = pog;
    lastScene = scene;
    return scene;
  };

  return { getStats: () => stats, project };
};

export const projectPlanogramScene = (
  pog: Pog,
  label: PlanogramProductLabel = "name",
): PlanogramScene => projectPlanogramSceneUncached(pog, label).scene;
